import React, { useEffect, useMemo } from 'react';
import { Alert, MenuItem, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as api from '../../../api/index.js';
import { getGroups, getUserProvider } from '../../../actions/auth.js';
import { CREATE_PLAYER, UPDATE_PLAYER } from '../../../constants/actionTypes.js';
import { discipline_all, season_all, getActualSeason, getSeasonDates } from '../../../bdd/bddSTUB.js';
import { usePermissions } from '../../../permissions/usePermissions.js';
import { PERMISSIONS } from '../../../permissions/permissionKeys.js';
import useStyles from '../../../styles/global-mui.js';
import TransfersView from './TransfersView.jsx';
import { createTransferApi } from './transferApi.mjs';
import { idOf } from './transferModel.mjs';

export default function GetTransfers({ onDirtyChange }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { getPermission } = usePermissions();
  const groups = useSelector(state => state.authentication.groups) || [];
  const currentUser = useSelector(state => state.authentication.currentUser);
  const providerAllowed = !!getPermission(PERMISSIONS.FILTER_PROVIDER)?.allowed;
  const canSave = !!getPermission(PERMISSIONS.SPORT_PLAYER_EDIT_PAGE)?.allowed;
  const canCreate = !!getPermission(PERMISSIONS.SPORT_PLAYER_CREATE_PAGE)?.allowed &&
    !!getPermission(PERMISSIONS.SPORT_PLAYER_HOME_BUTTON_CREATE)?.allowed;
  const provider = idOf(getUserProvider());
  const params = new URLSearchParams(location.search);
  const discipline = params.get('discipline') || '';
  const requestedSeason = params.get('season');
  const season = season_all.some(s => idOf(s) === requestedSeason) ? requestedSeason : idOf(getActualSeason());
  useEffect(() => { if (providerAllowed) dispatch(getGroups()); }, [dispatch, providerAllowed]);
  const transferApi = useMemo(() => createTransferApi({
    // Bornes de saison seulement par défaut (ex. recherche d'une arrivée par équipe).
    // Les dates explicites de l'effectif doivent conserver la priorité.
    api: { ...api, fetchPlayers: filters => api.fetchPlayers({
      ...(filters.season && filters.teams ? getSeasonDates(filters.season) : {}), ...filters }) },
    onSaved: (player, isNew) => dispatch({ type: isNew ? CREATE_PLAYER : UPDATE_PLAYER, payload: player }),
  }), [dispatch, provider, currentUser?.sub]);
  if (!provider) return <Alert severity="warning">Le groupe de l’utilisateur n’est pas encore disponible.</Alert>;
  return <TransfersView key={`${currentUser?.sub || ''}:${provider}`} api={transferApi}
    initialProvider={provider} initialDiscipline={discipline_all.some(d => idOf(d) === discipline) ? discipline : ''}
    initialSeason={season} getSeasonDates={getSeasonDates} disciplines={discipline_all} seasons={season_all} filterInputClassName={classes.customInput}
    canSave={canSave} canCreate={canCreate} onDirtyChange={onDirtyChange}
    onFiltersApplied={filters => {
      const query = new URLSearchParams({ discipline: filters.discipline, season: filters.season });
      navigate({ pathname: location.pathname, search: query.toString() }, { replace: true });
    }}
    renderProvider={providerAllowed ? ({ value, onChange, disabled }) =>
      <TextField select fullWidth size="medium" className={classes.customInput} label="Groupe" value={value} disabled={disabled}
        SelectProps={{ MenuProps: { className: 'transfers-filter-menu' } }} onChange={e => onChange(e.target.value)}>
        {!groups.some(g => idOf(g) === value) && <MenuItem value={value}>{value}</MenuItem>}
        {[...groups].sort((a, b) => a.name.localeCompare(b.name, 'fr')).map(g => <MenuItem key={idOf(g)} value={idOf(g)}>{g.name}</MenuItem>)}
      </TextField> : undefined} />;
}
